/**
 * FS-03 전시관 — 순수 로직(시험 대상): 걷기 · 받침대 접근 · 작동 타이머 · 막힘 · 스티커(내 반응) · 친구 자리 5.
 * 점수·순위·비교 없음(DR-01): 반응 개수는 내 작품에만, 목록은 서버 순서(반응순 정렬 없음 — SR-SOC-02).
 */
import type { KernelEvent } from "@picnic-field/local-host/protocol";
import type {
  Point,
  Bounds,
  Pedestal,
  Activity,
  ActivityMap,
  GalleryEventResult,
  ReactionMap,
  ReactionOp,
} from "../types";

// ── 걷기(이동: 끌기 · 방향키/WASD) ────────────────────────────────────────

/** 걷는 속력(m/s) — 받침대 간격(가짜 0.9m · 실제 전시관 모드 2m)을 1~2초 */
export const WALK_SPEED = 1.4;
/** 받침대에 이만큼 다가가면 작품 카드(m) */
export const APPROACH_M = 0.55;

export const clampTo = (b: Bounds, p: Point): Point => ({
  x: Math.max(b.minX, Math.min(b.maxX, p.x)),
  z: Math.max(b.minZ, Math.min(b.maxZ, p.z)),
});

/**
 * 누르고 있는 방향 키 → 이동 방향. 카메라는 남쪽에서 북쪽(+z)을 본다(오른손 Y-up) — 화면 위 = +z, 화면 오른쪽 = −x.
 */
export function keyDir(keys: ReadonlySet<string>): Point {
  let x = 0;
  let z = 0;
  if (keys.has("ArrowUp") || keys.has("KeyW")) z += 1;
  if (keys.has("ArrowDown") || keys.has("KeyS")) z -= 1;
  if (keys.has("ArrowRight") || keys.has("KeyD")) x -= 1;
  if (keys.has("ArrowLeft") || keys.has("KeyA")) x += 1;
  const n = Math.hypot(x, z);
  return n ? { x: x / n, z: z / n } : { x: 0, z: 0 };
}

export function walk(b: Bounds, p: Point, dir: Point, dtS: number): Point {
  return clampTo(b, { x: p.x + dir.x * WALK_SPEED * dtS, z: p.z + dir.z * WALK_SPEED * dtS });
}

/**
 * 끌기 → 이동(화면 px → m). 바닥을 잡아 끄는 느낌: 손가락을 오른쪽으로 끌면 보는 곳은 왼쪽으로(지도 끌기와 같게).
 * pxPerM 은 화면 높이에 맞춘 대략값(카메라 3/4 시점).
 */
export function dragTo(b: Bounds, start: Point, dxPx: number, dyPx: number, pxPerM: number): Point {
  // 화면 오른쪽 = −x 이므로 오른쪽으로 끌면(보는 곳이 왼쪽으로) +x
  return clampTo(b, { x: start.x + dxPx / pxPerM, z: start.z + dyPx / pxPerM });
}

/** 위에서 본 그림(미니맵 · 배치도) 가로 비율 0~1 — 화면과 같은 방향(오른쪽 = −x) */
export const planX = (b: Bounds, x: number) => (b.maxX - x) / Math.max(1e-6, b.maxX - b.minX);
/** 세로 비율 0~1 — 위 = 북쪽(+z) */
export const planY = (b: Bounds, z: number) => (b.maxZ - z) / Math.max(1e-6, b.maxZ - b.minZ);

export function nearest(ps: readonly Pedestal[], p: Point, max = APPROACH_M): Pedestal | null {
  let best: Pedestal | null = null;
  let bd = max;
  for (const q of ps) {
    const d = Math.hypot(q.x - p.x, q.z - p.z);
    if (d <= bd) {
      best = q;
      bd = d;
    }
  }
  return best;
}

export function boundsOf(ps: readonly Pedestal[], pad = 0.7): Bounds {
  if (!ps.length) return { minX: -2, maxX: 2, minZ: -1, maxZ: 3 };
  const xs = ps.map((p) => p.x);
  const zs = ps.map((p) => p.z);
  return {
    minX: Math.min(...xs) - pad,
    maxX: Math.max(...xs) + pad,
    minZ: Math.min(...zs) - pad - 0.4,
    maxZ: Math.max(...zs) + pad,
  };
}

/** 받침대 앞(남쪽) 선 자리 — 카드를 열 때 그 앞으로 걸어간다 */
export const standBefore = (p: Pedestal): Point => ({ x: p.x, z: p.z - 0.4 });

// ── 작동(60초) · 막힘(빨간 포트) ──────────────────────────────────────────

/** 커널 이벤트 → 작동 상태(전시관 activated · deactivated · activate-busy · stall-other) */
export function onGalleryEvent(
  active: ActivityMap,
  e: KernelEvent,
  now: number,
): GalleryEventResult {
  switch (e.type) {
    case "activated": {
      const cur = active[e.owner];
      return {
        active: {
          ...active,
          [e.owner]: {
            until: now + e.seconds * 1000,
            seconds: e.seconds,
            stalled: cur?.stalled ?? [],
          },
        },
        busy: null,
      };
    }
    case "deactivated": {
      const next = { ...active };
      delete next[e.owner];
      return { active: next, busy: null };
    }
    case "activate-busy":
      return { active, busy: { owner: e.owner, limit: e.limit } };
    case "stall-other": {
      const cur = active[e.owner];
      if (!cur) return { active, busy: null };
      const stalled = e.cleared
        ? cur.stalled.filter((p) => p !== e.port)
        : cur.stalled.includes(e.port)
          ? cur.stalled
          : [...cur.stalled, e.port];
      return { active: { ...active, [e.owner]: { ...cur, stalled } }, busy: null };
    }
    default:
      return { active, busy: null };
  }
}

/** 남은 초(표시용 — 올림) */
export const secondsLeft = (a: Activity | undefined, now: number) =>
  a ? Math.max(0, Math.min(a.seconds ?? Infinity, Math.ceil((a.until - now) / 1000))) : 0;

// ── 스티커(내 반응) — 낙관적 갱신 · 실패하면 되돌림 ───────────────────────────

/**
 * 누른 스티커 → 할 일: 없으면 붙이기, 다른 것이면 바꾸기(PUT), 같은 것을 다시 누르면 떼기(DELETE).
 * 내 작품에는 붙일 수 없다(SR-SOC-02 · 서버 409).
 */
export function reactionOp(
  current: string | null,
  snapshotId: string,
  stickerKey: string,
  mine: boolean,
): ReactionOp | null {
  if (mine) return null;
  if (current === stickerKey) return { kind: "delete", snapshotId };
  return { kind: "put", snapshotId, stickerKey };
}

export function applyReaction(m: ReactionMap, op: ReactionOp): ReactionMap {
  return { ...m, [op.snapshotId]: op.kind === "put" ? op.stickerKey : null };
}

// ── 친구 자리(FS-03-03 — 최대 5대) ──────────────────────────────────────

export function addFriend(
  slots: readonly string[],
  id: string,
  max: number,
  mine: ReadonlySet<string>,
): { slots: string[]; full: boolean } {
  if (mine.has(id) || slots.includes(id)) return { slots: [...slots], full: false };
  if (slots.length >= max) return { slots: [...slots], full: true };
  return { slots: [...slots, id], full: false };
}

export const removeFriend = (slots: readonly string[], id: string) => slots.filter((x) => x !== id);

/**
 * 기기 힘 게이지(0~1) — 움직이는 기계 수가 예산(내 기계 + 친구 5대 = 6)의 얼마를 쓰는가.
 * 성능이 떨어지면(측정 fps p5 < 30) 열화 상태로 바꾸고 그림 품질을 낮춘다(장식부터 — SCR '느려지면 … 자동 조정').
 */
export function loadGauge(friends: number, max: number): number {
  return Math.min(1, (1 + friends) / (1 + max));
}

/** 1초 창 fps 목록 → 열화 여부(최근 3창 중 둘 이상이 30 미만) */
export function degraded(fpsWindows: readonly number[]): boolean {
  const last = fpsWindows.slice(-3);
  return last.length >= 2 && last.filter((f) => f < 30).length >= 2;
}
